import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AddMemberInput, UpdateMemberInput, Role } from '@bom/shared';
import { api } from '../lib/apiClient';

/** 成员（membership）项目：user_id + role + status + added_at（无 email/name，需 user_id 已知） */
export interface MemberItem {
  user_id?: string;
  name?: string; // 后端 join 用户资料带回
  email?: string;
  SK?: string; // TENANT#<tid>
  GSI3SK?: string; // USER#<uid>
  role: Role;
  status: 'active' | 'disabled';
  added_at?: number;
}

/** 可分配用户（供“添加成员”下拉选人）：最小字段，租户管理员即可获取 */
export interface AssignableUser {
  id: string;
  name: string;
  email: string;
  is_platform_admin: boolean;
}
export function useAssignableUsers(tid: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['assignable-users', tid],
    queryFn: () =>
      api.get<{ users: AssignableUser[] }>(`/tenants/${tid}/assignable-users`).then((r) => r.users),
    enabled: !!tid && enabled,
  });
}

export function useMembers(tid: string | undefined) {
  return useQuery({
    queryKey: ['members', tid],
    queryFn: () =>
      api.get<{ members: MemberItem[] }>(`/tenants/${tid}/members`).then((r) => r.members),
    enabled: !!tid,
  });
}

export function useAddMember(tid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddMemberInput) => api.post(`/tenants/${tid}/members`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members', tid] }),
  });
}

export function useUpdateMember(tid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { uid: string; body: UpdateMemberInput }) =>
      api.patch<void>(`/tenants/${tid}/members/${vars.uid}`, vars.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members', tid] }),
  });
}

export function useRemoveMember(tid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uid: string) => api.del<void>(`/tenants/${tid}/members/${uid}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members', tid] }),
  });
}
